import { generateId } from '@freelancer/datastore/testing/helpers';
import type { RemovalReasonApi } from 'api-typings/common/common';
import {
  ContextTypeApi,
  SourceTypeApi,
} from 'api-typings/messages/messages_types';
import { MilestoneActionApi } from 'api-typings/projects/projects';
import {
  NetPromoterScoreSourceApi,
  NetPromoterScoreTypeApi,
} from 'api-typings/users/users';
import type { Bid } from '../bids/bids.model';
import type { MilestoneRequest } from '../milestone-requests/milestone-requests.model';
import type { Milestone } from '../milestones/milestones.model';
import type { Project } from '../projects';
import {
  MessageSendStatus,
  type Message,
  type MessageFileAttachment,
  type MessageVersion,
} from './messages.model';
import type { RichMessage, RichMessageGroup } from './rich-message.model';
import type { Thread } from '../threads/threads.model';
import type { User } from '../users/users.model';

export type GenerateMessagesOptions = {
  readonly fromUserId: number;
  readonly threadId: number;
} & (
  | {
      readonly messages: readonly string[];
    }
  | {
      messages(index: number): string;
      readonly count: number;
    }
);

export function generateMessages(
  options: GenerateMessagesOptions,
): readonly Message[] {
  if ('count' in options) {
    const { fromUserId, threadId, messages, count } = options;
    return Array.from({ length: count }).map((_, index) =>
      generateMessage({ fromUserId, threadId, message: messages(index) }),
    );
  }

  const { fromUserId, threadId, messages } = options;
  return messages.map(message =>
    generateMessage({ fromUserId, threadId, message }),
  );
}

export interface GenerateMessageOptions {
  readonly message: string;
  readonly attachments?: readonly string[];
  readonly files?: readonly MessageFileAttachment[];
  readonly richMessage?: RichMessage;
  readonly threadId: number;
  readonly fromUserId: number;
  readonly bid?: Bid;
  readonly removeReason?: RemovalReasonApi;
  readonly timeCreated?: number;
  readonly quotedMessageId?: number;
  readonly sendStatus?: MessageSendStatus;
  readonly versions?: readonly MessageVersion[];
  readonly timeEdited?: number;
}

export function generateMessage({
  fromUserId,
  attachments = [],
  files,
  threadId,
  message,
  richMessage,
  removeReason,
  timeCreated = Date.now(),
  quotedMessageId,
  sendStatus = MessageSendStatus.SENDING,
  versions,
  timeEdited,
}: GenerateMessageOptions): Message {
  return {
    id: generateId(),
    attachments,
    files,
    fromUser: fromUserId,
    message,
    messageSource: SourceTypeApi.BOT,
    richMessage,
    threadId,
    timeCreated,
    sendStatus,
    removeReason,
    quotedMessageId,
    versions,
    timeEdited,
  };
}

// #region Mixins

export function sentMessage(): Pick<GenerateMessageOptions, 'sendStatus'> {
  return { sendStatus: MessageSendStatus.SENT };
}

export function withEditHistory({
  original,
  edits,
  timeCreated = Date.now(),
  editInterval = 1000,
}: {
  readonly original: string;
  readonly edits: readonly string[];
  readonly timeCreated?: number;
  readonly editInterval?: number;
}): Pick<
  GenerateMessageOptions,
  'message' | 'sendStatus' | 'timeCreated' | 'timeEdited' | 'versions'
> {
  const versions = [original, ...edits];

  const versionTime = (index: number): number =>
    timeCreated + index * editInterval;

  return {
    ...sentMessage(),
    message: edits.at(-1) ?? original,
    timeCreated,
    timeEdited: versionTime(versions.length - 1),
    versions: versions.map((message, index) => ({
      version: index + 1,
      message,
      timeCreated: versionTime(index),
    })),
  };
}

/**
 * Generate a rich message for an awarded project.
 *
 * FIXME: Support custom budgets and some of the other 10 different
 * combinations of messages from consumers/AcceptRichMessageConsumer.php
 */
export function awardProjectRichMessage({
  thread,
  freelancerBotUser,
  bidId,
}: {
  readonly thread: Thread;
  readonly freelancerBotUser: User;
  readonly bidId: number;
}): GenerateMessageOptions {
  if (thread.context.type !== ContextTypeApi.PROJECT) {
    throw new Error('Thread does not belong to a project context.');
  }

  return {
    fromUserId: freelancerBotUser.id,
    threadId: thread.id,
    message: `You've been awarded! This project has a budget of $300.00 USD and you will be charged a project commission of $30.00 USD. To accept click here https://www.syd1.fln-dev.net/projects/${thread.context.id}.html`,
    richMessage: {
      category: `ACCEPT_CTA_${thread.context.id}`,
      long: [
        [
          {
            text: 'Congratulations, your bid has been awarded!',
            type: 'heading',
          },
        ],
        [
          {
            text: 'Click the Accept button below to start working.',
            type: 'body',
          },
        ],
        [
          {
            text: 'This project has a budget of $300.00 USD and you will be charged a project commission of $30.00 USD.',
            style: 'small',
            type: 'body',
          },
        ],
        [
          {
            action: 'PATCH',
            type: 'button',
            label: 'Accept',
            request: {
              url: `https://www.syd1.fln-dev.net/api/projects/0.1/bids/${bidId}/`,
              payload: { action: 'ACCEPT' },
              contentType: 'application/x-www-form-urlencoded',
            },
            displayType: 'green',
          },
        ],
      ],
      short: [],
      email: {
        text: `Follow the link below to accept this project and start working!<br/><br/>https://www.syd1.fln-dev.net/api/projects/0.1/bids/${bidId}/`,
        type: 'email-copy',
        heading: 'Congratulations, your bid has been awarded!',
      },
    },
  };
}

export function contestAcceptOfferFreelancerRichMessage({
  thread,
  freelancerBotUser,
  buyerPublicName,
  contestId,
  entryNumber,
  entryId,
  offeredPriceFormatted,
}: {
  readonly thread: Thread;
  readonly freelancerBotUser: User;
  readonly buyerPublicName: string;
  readonly entryId: number;
  readonly entryNumber: number;
  readonly contestId: number;
  readonly offeredPriceFormatted: string;
}): GenerateMessageOptions {
  return {
    fromUserId: freelancerBotUser.id,
    threadId: thread.id,
    message: `${buyerPublicName} offered to buy your entry for ${offeredPriceFormatted}. You can accept this on the entry's card in the contest's page, or by clicking the button below.`,
    richMessage: {
      category: `CONTEST_ACCEPTOFFER_${entryId}`,
      long: [
        [
          {
            type: 'button',
            action: 'REDIRECT',
            displayType: 'link-heading' as any,
            label: `You were offered ${offeredPriceFormatted} for Entry ${entryNumber}`,
            request: {
              url: `/contest/${contestId}.html?tab=entries&acceptOfferForEntry=${entryId}`,
              payload: {},
            },
          },
        ],
        [
          {
            type: 'body',
            text: `${buyerPublicName} offered to buy your entry for ${offeredPriceFormatted}. You can accept this on the entry's card in the contest's page, or by clicking the button below.`,
          },
        ],
        [
          {
            type: 'button',
            action: 'REDIRECT',
            displayType: 'success',
            label: 'Accept Offer',
            request: {
              url: `/contest/${contestId}.html?tab=entries&acceptOfferForEntry=${entryId}`,
              payload: {},
            },
          },
        ],
      ],
      short: [],
      email: {} as any,
    },
  };
}
export function contestAcceptOfferEmployerRichMessage({
  thread,
  freelancerBotUser,
  contestId,
  sellerPublicName,
  entryId,
  entryNumber,
  offeredPriceFormatted,
}: {
  readonly thread: Thread;
  readonly freelancerBotUser: User;
  readonly sellerPublicName: string;
  readonly entryId: number;
  readonly entryNumber: number;
  readonly contestId: number;
  readonly offeredPriceFormatted: string;
}): GenerateMessageOptions {
  return {
    fromUserId: freelancerBotUser.id,
    threadId: thread.id,
    message: 'FIXME',
    richMessage: {
      category: `CONTEST_ACCEPTOFFER_${entryId}`,
      long: [
        [
          {
            type: 'body',
            text: `${sellerPublicName} accepted to sell their Entry #${entryNumber} for '${offeredPriceFormatted}! Buy the entry now to proceed with handover.`,
          },
        ],
        [
          {
            type: 'button',
            action: 'REDIRECT',
            displayType: 'success' as any,
            label: `Buy Now for ${offeredPriceFormatted}`,
            request: {
              url: `/contest/${contestId}.html?tab=entries&buyEntry=${entryId}`,
              payload: {},
            },
          },
        ],
      ],
      short: [],
      email: {} as any,
    },
  };
}
export function offsitingMessageReminderFreelancerRichMessage({
  thread,
  freelancerBotUser,
  offsiteMessageId = generateId(),
}: {
  readonly thread: Thread;
  readonly freelancerBotUser: User;
  readonly offsiteMessageId?: number;
}): GenerateMessageOptions {
  return {
    fromUserId: freelancerBotUser.id,
    threadId: thread.id,
    message: 'FIXME',
    richMessage: {
      category: `OFFSITING_MESSAGE_REMINDER_FREELANCER_${offsiteMessageId}`,
      long: [
        [
          {
            type: 'heading',
            text: 'Always communicate and make payments through Freelancer.com',
          },
        ],
        [
          {
            type: 'body',
            text: 'Stay protected from scams and ensure you get paid by only communicating using the Freelancer.com chat system.',
          },
        ],
        [
          {
            type: 'body',
            style: 'italic',
            text: 'Communicating or receiving payments outside Freelancer.com is a violation of our Terms and Conditions. Heavy penalties will apply.',
          },
        ],
        [
          {
            type: 'button',
            displayType: 'success' as any,
            label: 'View Terms and Conditions',
            action: 'REDIRECT',
            request: {
              url: '/about/terms/',
              payload: {},
            },
          },
        ],
      ],
      short: [],
      email: {} as any,
    },
  };
}
export function createCanvasRichMessage({
  thread,
  freelancerBotUser,
  creator,
  canvasId,
}: {
  readonly thread: Thread;
  readonly freelancerBotUser: User;
  readonly creator: User;
  readonly canvasId: number;
}): GenerateMessageOptions {
  return {
    fromUserId: freelancerBotUser.id,
    threadId: thread.id,
    message: `Sketch ideas, share notes, and collaborate in real time. You've been invited to a Prototyper board by @${creator.username}.`,
    richMessage: {
      category: `CREATE_CANVAS_${canvasId}`,
      long: [
        [
          {
            type: 'heading',
            text: 'Prototyper Board',
          },
          {
            type: 'body',
            text: `Sketch ideas, share notes, and collaborate in real time. You've been invited to a Prototyper board by @${creator.username}.`,
          },
        ],
        [
          {
            type: 'button',
            action: 'REDIRECT',
            displayType: 'primary' as any,
            label: 'Join Prototyper',
            request: {
              url: `/board/${canvasId}`,
              payload: {},
            },
          },
        ],
      ],
      short: [],
      email: {} as any,
    },
  };
}
/**
 * Generate a rich message for a milestone release request.
 */
export function createMilestoneReleaseRequestRichMessage({
  thread,
  freelancerUser,
  freelancerBotUser,
  milestone,
  projectId,
}: {
  readonly thread: Thread;
  readonly freelancerBotUser: User;
  readonly freelancerUser: User;
  readonly milestone: Milestone;
  readonly projectId: number;
}): GenerateMessageOptions {
  const richMessageSections: RichMessageGroup = [
    [
      {
        type: 'heading',
        text: `Milestone release requested by @${freelancerUser.username}`,
      },
    ],
    [
      {
        type: 'link-with-text',
        link: `/projects/${projectId}/payments`,
        linkText: `${milestone.description}`,
        text: `${milestone.currency?.sign}${milestone.amount} ${milestone.currency?.code}`,
      },
    ],
    [
      {
        type: 'button',
        action: 'REDIRECT',
        displayType: 'default',
        label: 'Release Milestone',
        request: {
          url: '',
          payload: {
            action: MilestoneActionApi.RELEASE,
            transaction_id: milestone.id,
          },
        },
      },
    ],
  ];

  return {
    fromUserId: freelancerBotUser.id,
    threadId: thread.id,
    message: 'Request Milestone Release',
    richMessage: {
      category: `request_payment_milestone`,
      long: richMessageSections,
      short: richMessageSections,
      email: {} as any,
    },
  };
}

export function createMilestoneRequestRichMessage({
  thread,
  freelancerUser,
  freelancerBotUser,
  milestoneRequest,
  projectId,
}: {
  readonly thread: Thread;
  readonly freelancerBotUser: User;
  readonly freelancerUser: User;
  readonly milestoneRequest: MilestoneRequest;
  readonly projectId: number;
}): GenerateMessageOptions {
  const richMessageSections: RichMessageGroup = [
    [
      {
        type: 'heading',
        text: `Milestone requested by @${freelancerUser.username}`,
      },
    ],
    [
      {
        type: 'link-with-text',
        link: `/projects/${projectId}/payments`,
        linkText: `${milestoneRequest.description}`,
        text: `${milestoneRequest.currency?.sign}${milestoneRequest.amount} ${milestoneRequest.currency?.code}`,
      },
    ],
    [
      {
        type: 'button',
        action: 'REDIRECT',
        displayType: 'default',
        label: 'Accept',
        request: {
          url: '',
          payload: {
            action: 'ACCEPT',
            milestone_request_id: milestoneRequest.id,
            project_id: milestoneRequest.projectId,
            amount: milestoneRequest.amount,
            description: milestoneRequest.description ?? '',
            currency_id: milestoneRequest.currency?.id ?? 1,
          },
        },
      },
    ],
    [
      {
        type: 'button',
        action: 'REDIRECT',
        displayType: 'default',
        label: 'Reject',
        request: {
          url: '',
          payload: {
            action: 'REJECT',
            milestone_request_id: milestoneRequest.id,
          },
        },
      },
    ],
  ];

  return {
    fromUserId: freelancerBotUser.id,
    threadId: thread.id,
    message: 'Milestone requested',
    richMessage: {
      category: `request_milestone_${projectId}`,
      long: richMessageSections,
      short: richMessageSections,
      email: {} as any,
    },
  };
}

/**
 * Generate a rich message for customer satisfaction feedback after a support chat.
 */
export function createCustomerSatisfactionFeedbackRichMessage({
  thread,
  freelancerBotUser,
  agentName,
  agentSessionId,
  richMessageId,
  linkUrl = 'https://www.trustpilot.com/evaluate/www.freelancer.com',
  npsType = NetPromoterScoreTypeApi.SUPPORT_QUEUE,
  npsSource = NetPromoterScoreSourceApi.RICH_MESSAGE_ON_RESOLVE,
}: {
  readonly thread: Thread;
  readonly freelancerBotUser: User;
  readonly agentName: string;
  readonly agentSessionId: number;
  readonly richMessageId: number;
  readonly linkUrl?: string;
  readonly npsType?: string;
  readonly npsSource?: string;
}): GenerateMessageOptions {
  const richMessageSections: RichMessageGroup = [
    [
      {
        type: 'heading-centered',
        text: `How was your chat with ${agentName}?`,
        hide_on_success: true,
      },
    ],
    [
      {
        type: 'csat-score-type',
        name: 'score',
        hide_on_success: true,
      },
    ],
    [
      {
        type: 'text-input',
        name: 'comment',
        placeholder: 'Leave a comment (Optional)',
        size: 'small',
        hide_on_success: true,
      },
    ],
    [
      {
        type: 'button',
        action: 'POST',
        displayType: 'default',
        label: 'Submit',
        request: {
          url: '/ajax-api/user/nps.php',
          payload: {
            toEntityId: agentSessionId,
            type: npsType,
            source: npsSource,
            sourceId: richMessageId,
          },
        },
        errors: {
          INVALID_NPS_SCORE: 'Please provide a rating.',
        },
        hide_on_success: true,
      },
    ],
    [
      {
        text: 'Thank you for your feedback!',
        type: 'alert',
        alert_type: 'success',
        show_on_success: true,
        show_when: {
          score: 1,
        },
      },
    ],
    [
      {
        text: `We're thrilled you had a great experience with us! If you could spare a moment, we'd love your feedback on ${linkUrl}. Your words inspire us and help others. Thank you!`,
        type: 'alert',
        alert_type: 'success',
        show_on_success: true,
        show_when: {
          score: 5,
        },
      },
    ],
  ];

  return {
    fromUserId: freelancerBotUser.id,
    threadId: thread.id,
    message:
      "Help us improve! We'd love to get feedback on your support chat experience.",
    richMessage: {
      category: `CustomerSatisfactionFeedbackMessage-SUPPORT_CHAT-${agentSessionId}`,
      long: richMessageSections,
      short: richMessageSections,
      email: {
        type: 'email-copy',
        heading: 'Customer Satisfaction Feedback',
        text: "We'd love to hear how you've found the support chat experience so far.",
      } as any,
    },
  };
}

/**
 * Generate a rich message for Freightlancer concierge out of office notification.
 */
export function createFreightlancerConciergeOutOfOfficeRichMessage({
  thread,
  agentUser,
}: {
  readonly thread: Thread;
  readonly agentUser: User;
}): GenerateMessageOptions {
  const LOADSHIFT_SUPPORT_EMAIL = 'support@loadshift.com.au';
  const LOADSHIFT_SUPPORT_NUMBER = '1300 562 374';

  return {
    fromUserId: agentUser.id,
    threadId: thread.id,
    message: `Your freight coordinator is currently out of office and will respond to your message as soon as possible. For any urgent queries, please contact ${LOADSHIFT_SUPPORT_EMAIL} or call ${LOADSHIFT_SUPPORT_NUMBER}, referencing your load ID.`,
    richMessage: {
      category: `FREIGHTLANCER_CONCIERGE_OUT_OF_OFFICE`,
      long: [
        [
          {
            type: 'body-with-dynamic-links',
            text: `Your freight coordinator is currently out of office and will respond to your message as soon as possible. For any urgent queries, please contact ${LOADSHIFT_SUPPORT_EMAIL} or call ${LOADSHIFT_SUPPORT_NUMBER}, referencing your load ID.`,
            style: 'italic',
          },
        ],
      ],
      short: [],
      email: {
        type: 'email-copy',
        heading: 'Out of office',
        body: `Your freight coordinator is currently out of office and will respond to your message as soon as possible. For any urgent queries, please contact <a href='mailto:${LOADSHIFT_SUPPORT_EMAIL}'>${LOADSHIFT_SUPPORT_EMAIL}</a> or call <a href='tel:${LOADSHIFT_SUPPORT_NUMBER}'>${LOADSHIFT_SUPPORT_NUMBER}</a>, referencing your load ID.`,
      } as any,
    },
  };
}

/**
 * Generate a rich message for a milestone release reminder.
 */
export function createMilestoneReleaseReminderRichMessage({
  thread,
  freelancerUser,
  freelancerBotUser,
  milestone,
  projectId,
  releaseRequestTime,
}: {
  readonly thread: Thread;
  readonly freelancerBotUser: User;
  readonly freelancerUser: User;
  readonly milestone: Milestone;
  readonly projectId: number;
  readonly releaseRequestTime: number;
}): GenerateMessageOptions {
  const richMessageSections: RichMessageGroup = [
    [
      {
        type: 'heading',
        text: `Milestone release requested 3 days ago.`,
      },
    ],
    [
      {
        type: 'body',
        text: `Please release milestone to allow ${freelancerUser.username} to keep working.`,
      },
    ],
    [
      {
        type: 'link-with-text',
        link: `/projects/${projectId}/payments`,
        linkText: `${milestone.description}`,
        text: `${milestone.currency?.sign}${milestone.amount} ${milestone.currency?.code}`,
      },
    ],
    [
      {
        type: 'button',
        action: 'REDIRECT',
        displayType: 'default',
        label: 'Release Milestone',
        request: {
          url: '',
          payload: {
            action: MilestoneActionApi.RELEASE,
            transaction_id: milestone.id,
          },
        },
      },
    ],
  ];

  return {
    fromUserId: freelancerBotUser.id,
    threadId: thread.id,
    message: 'Milestone Release Reminder',
    richMessage: {
      category: `release_milestone_reminder`,
      long: richMessageSections,
      short: richMessageSections,
      email: {} as any,
    },
  };
}

/**
 * Generate a rich message for a accepting an set of external credentials.
 */
export function createExternalCredentialRichMessage({
  thread,
  freelancerBotUser,
  freelancerUser,
  contextId,
  project,
}: {
  readonly thread: Thread;
  readonly freelancerBotUser: User;
  readonly freelancerUser: User;

  readonly contextId: number;
  readonly project: Project;
}): GenerateMessageOptions {
  return {
    fromUserId: freelancerBotUser.id,
    threadId: thread.id,
    message: 'Project Login Details',
    richMessage: {
      category: `CREATE_ALLOCATION_project_${project.id}`,
      long: [
        [{ type: 'heading', text: 'Project Login Details' }],
        [
          {
            type: 'body',
            text: `Hi ${freelancerUser.username},\n\n Your login details for the project "…\n\nClick below to view them so you can start work.`,
          },
        ],
        [
          {
            action: 'REDIRECT',
            label: 'View Login Details',
            request: {
              url: '',
              payload: {
                action: 'ALLOCATE-EXTERNAL-CREDENTIALS',
                context_id: contextId,
              },
            },
            type: 'button',
            displayType: 'default',
          },
        ],
      ],
      short: [],
      email: {} as any,
    },
  };
}

export function createViewMilestoneRichMessage({
  thread,
  freelancerBotUser,
  freelancer,
  projectId,
  milestoneRequestId,
  milestone,
}: {
  readonly thread: Thread;
  readonly freelancerBotUser: User;
  readonly freelancer: User;
  readonly projectId: number;
  readonly milestoneRequestId: number;
  readonly milestone: Milestone;
}): GenerateMessageOptions {
  return {
    fromUserId: freelancerBotUser.id,
    threadId: thread.id,
    message: `${freelancer.displayName} requested a milestone payment`,
    richMessage: {
      category: `CREATE_VIEW_MILESTONE_${milestone.id}`,
      long: [
        [
          {
            type: 'heading',
            text: `${freelancer.displayName} requested a milestone payment`,
          },
          {
            type: 'body',
            text: `Milestone: ${milestone.description}\nAmount: ${milestone.amount} ${milestone.currency}`,
          },
        ],
        [
          {
            type: 'button',
            action: 'REDIRECT',
            displayType: 'primary' as any,
            label: 'View Milestone',
            request: {
              url: `/projects/${projectId}/payments#milestone-${milestoneRequestId}`,
              payload: {},
            },
          },
        ],
      ],
      short: [],
      email: {} as any,
    },
  };
}
// #endregion
